/**
 * Session state for the exit ladder and the promo countdown.
 *
 * The ladder from the handoff is: paywall → one interception → one discount
 * step → leaving is allowed. Each rung is a flag that is set the first time it
 * fires and never cleared inside the session, which is what makes the ladder a
 * ladder rather than a loop the user cannot get out of.
 *
 * `sessionStorage` rather than state: a reload must not hand the user a second
 * interception, and must not restart a countdown they already watched run down.
 */
import React from 'react';
import { PROMO_MINUTES } from '../config/paywall';

const KEY = {
    exitIntent: 'dys_pw_exit_intent_shown',
    downsell: 'dys_pw_downsell_shown',
    granted: 'dys_pw_promo_granted_at',
    promo: 'dys_pw_promo',
    pending: 'dys_pw_checkout_pending',
} as const;

function read(key: string): string | null {
    try {
        return window.sessionStorage.getItem(key);
    } catch {
        return null;
    }
}

function write(key: string, value: string): void {
    try {
        window.sessionStorage.setItem(key, value);
    } catch {
        /* private mode — the ladder degrades to "shows once per page view" */
    }
}

function drop(key: string): void {
    try {
        window.sessionStorage.removeItem(key);
    } catch {
        /* ignore */
    }
}

export const flags = {
    exitIntentShown: (): boolean => read(KEY.exitIntent) === '1',
    markExitIntentShown: (): void => write(KEY.exitIntent, '1'),
    downsellShown: (): boolean => read(KEY.downsell) === '1',
    markDownsellShown: (): void => write(KEY.downsell, '1'),
};

const WINDOW_MS = PROMO_MINUTES * 60 * 1000;

/** A stamp still inside its window, or null. Stale stamps read as absent. */
function liveGrant(): number | null {
    const v = Number(read(KEY.granted));

    if (!(v > 0)) return null;

    return Date.now() - v < WINDOW_MS ? v : null;
}

/**
 * When the promo was granted, as an epoch millisecond stamp.
 *
 * Within one visit the stamp is written once, so the countdown measures a real
 * deadline and does not restart whenever a screen remounts.
 *
 * A stamp left over from an EARLIER visit is replaced rather than honoured.
 * `sessionStorage` outlives reloads, so keeping it meant someone who opened the
 * paywall, went away, and came back was handed an offer that had already
 * expired — no countdown, no discounted row, and a checkout quietly at full
 * price, for a code Polar would still have accepted. A deadline nobody was
 * present for is not a deadline; it is a trap.
 */
export function grantPromo(): number {
    const live = liveGrant();

    if (live) return live;
    const now = Date.now();

    write(KEY.granted, String(now));

    return now;
}

export const promoGrantedAt = (): number | null => liveGrant();

/** The accepted promo, remembered so checkout and the paywall agree on it. */
export function saveAcceptedPromo(code: string, percent: number): void {
    write(KEY.promo, JSON.stringify({ code, percent }));
}

export function acceptedPromo(): { code: string; percent: number } | null {
    const raw = read(KEY.promo);

    if (!raw) return null;
    try {
        const v = JSON.parse(raw);

        return typeof v?.code === 'string' && typeof v?.percent === 'number' ? v : null;
    } catch {
        return null;
    }
}

/**
 * "We sent this person to Polar and they are not back with a success."
 *
 * Set immediately before leaving for the hosted checkout. The paywall reads it
 * on mount: still set means the browser came back without paying, which is the
 * cue for the recovery screen.
 */
export const markCheckoutPending = (): void => write(KEY.pending, String(Date.now()));
export const checkoutPending = (): boolean => !!read(KEY.pending);
export const clearCheckoutPending = (): void => drop(KEY.pending);

/** `mm:ss` left on the promo, and whether it has run out. Ticks every second. */
export function usePromoTimer(grantedAt: number | null): { label: string; expired: boolean; left: number } {
    const total = PROMO_MINUTES * 60 * 1000;
    const compute = React.useCallback(() => {
        if (!grantedAt) return total;

        return Math.max(0, total - (Date.now() - grantedAt));
    }, [grantedAt, total]);

    const [left, setLeft] = React.useState(compute);

    React.useEffect(() => {
        setLeft(compute());
        const id = setInterval(() => setLeft(compute()), 1000);

        return () => clearInterval(id);
    }, [compute]);

    const secs = Math.ceil(left / 1000);
    const mm = String(Math.floor(secs / 60)).padStart(2, '0');
    const ss = String(secs % 60).padStart(2, '0');

    return { label: `${mm}:${ss}`, expired: left <= 0, left };
}

/** Upward scroll speed, px/ms, that reads as "I'm heading for the exit". */
const FLICK_SPEED = 1.6;

/** Only a flick that lands near the top of the page counts. */
const FLICK_ZONE = 420;

/**
 * Fire `onExit` once when the person looks like they are leaving.
 *
 * Two signals, because pointer and touch leave differently:
 *   - pointer: the cursor exits through the top of the viewport, toward the
 *     tabs and the close button;
 *   - touch: a hard flick back up to the top of the page.
 *
 * **This must never touch history.** An earlier version pushed a sentinel entry
 * and consumed it with `history.back()` on teardown, which meant every state
 * change that disarmed the hook — starting checkout, opening the recovery
 * screen — silently navigated the person back a step. The handoff's rule that
 * the ladder leaves browser history untouched is not only about the last rung;
 * it is what keeps this hook from moving anyone anywhere.
 *
 * `onExit` is read through a ref so the caller can pass a fresh closure every
 * render without re-arming: re-arming is what made the teardown fire.
 */
export function useExitIntent(armed: boolean, onExit: () => void): void {
    const fired = React.useRef(false);
    const handler = React.useRef(onExit);

    handler.current = onExit;

    React.useEffect(() => {
        if (!armed) return undefined;
        fired.current = false;

        const trigger = () => {
            if (fired.current) return;
            fired.current = true;
            handler.current();
        };

        const onMouseOut = (e: MouseEvent) => {
            if (e.clientY <= 0 && !e.relatedTarget) trigger();
        };

        let lastY = window.scrollY;
        let lastT = Date.now();

        const onScroll = () => {
            const y = window.scrollY;
            const now = Date.now();
            const dt = now - lastT;

            if (dt > 0 && dt < 400) {
                const speed = (lastY - y) / dt;

                if (speed > FLICK_SPEED && y < FLICK_ZONE) trigger();
            }
            lastY = y;
            lastT = now;
        };

        document.addEventListener('mouseout', onMouseOut);
        window.addEventListener('scroll', onScroll, { passive: true });

        return () => {
            document.removeEventListener('mouseout', onMouseOut);
            window.removeEventListener('scroll', onScroll);
        };
    }, [armed]);
}
